/**
 * Transforms Next.js Image optimization URLs to direct CDN URLs in HTML.
 * This is necessary because Next.js Image components use relative paths that
 * don't work in static HTML contexts (like iframes with srcDoc).
 *
 * @param html - HTML string containing Next.js Image URLs
 * @param placeholderUrl - Optional placeholder URL to use if decoding fails
 * @param preserveSrcSet - If true, transforms srcSet URLs instead of removing them. Defaults to false.
 * @returns HTML with Next.js Image URLs replaced with direct CDN URLs
 */
export function transformNextImageToImg(
  html: string,
  placeholderUrl?: string,
  preserveSrcSet = false,
): string {
  const extractUrl = (params: string): string | null => {
    try {
      const searchParams = new URLSearchParams(params.replace(/&amp;/g, "&"));
      const urlParam = searchParams.get("url");
      return urlParam ? decodeURIComponent(urlParam) : null;
    } catch {
      return null;
    }
  };

  let processedHtml = html;
  if (preserveSrcSet) {
    processedHtml = processedHtml.replace(
      /srcset=["']([^"']*\/_next\/image\?[^"']*)["']/gi,
      (_match, srcSetValue) => {
        const transformedSrcSet = srcSetValue.replace(
          /((?:https?:\/\/[^/]+)?\/_next\/image\?)([^\s,]+)/gi,
          (_urlMatch: string, _prefix: string, params: string) => {
            const originalUrl = extractUrl(params);
            return originalUrl ?? placeholderUrl ?? _urlMatch;
          },
        );
        return `srcset="${transformedSrcSet}"`;
      },
    );
  }

  return processedHtml.replace(
    /<img([^>]+)src=["']((?:https?:\/\/[^/]+)?\/_next\/image\?)([^"']+)["']([^>]*)>/gi,
    (match, beforeSrc, _prefix, params, afterSrc) => {
      const originalUrl = extractUrl(params);

      if (!originalUrl) {
        if (placeholderUrl) {
          const cleaned = (beforeSrc + afterSrc)
            .replace(preserveSrcSet ? /(?!)/ : /srcset=["'][^"']*["']/gi, "")
            .replace(/\s{2,}/g, " ");
          return `<img${cleaned}src="${placeholderUrl}">`;
        }
        return match;
      }

      const cleaned = (beforeSrc + afterSrc)
        .replace(preserveSrcSet ? /(?!)/ : /srcset=["'][^"']*["']/gi, "")
        .replace(/\s{2,}/g, " ");

      return `<img${cleaned}src="${originalUrl}">`;
    },
  );
}

/**
 * Strips rich text formatting (HTML tags, markdown, etc.) to convert to plain text.
 * Useful for contexts that only accept plain text like Stripe product descriptions
 * or structured data.
 */
export function stripRichText(richText: string): string {
  if (!richText) {
    return richText;
  }

  return (
    richText
      // Remove HTML tags but preserve their text content
      .replace(/<[^>]*>/g, "")
      // Decode common HTML entities
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, " ")
      // Remove markdown formatting
      .replace(/\*\*(.*?)\*\*/g, "$1") // Bold
      .replace(/\*(.*?)\*/g, "$1") // Italic
      .replace(/__(.*?)__/g, "$1") // Bold
      .replace(/_(.*?)_/g, "$1") // Italic
      .replace(/~~(.*?)~~/g, "$1") // Strikethrough
      .replace(/`(.*?)`/g, "$1") // Inline code
      .replace(/```[\S\s]*?```/g, "") // Code blocks
      .replace(/^#{1,6}\s+/gm, "") // Headers
      .replace(/^\s*[*+-]\s+/gm, "• ") // List items
      .replace(/^\s*\d+\.\s+/gm, "") // Numbered lists
      .replace(/\[([^\]]+)]\([^)]+\)/g, "$1") // Links
      .replace(/!\[([^\]]*)]\([^)]+\)/g, "$1") // Images
      // Clean up extra whitespace
      .replace(/\n\s*\n/g, "\n\n") // Multiple newlines to double newlines
      .replace(/[\t ]+/g, " ") // Multiple spaces to single space
      .trim()
  );
}

/**
 * Injects CSS generated from a StyleLibrary into HTML head.
 * Used for applying design system styles to starter site previews.
 */
export function injectStyleLibraryCssIntoHtml(
  html: string,
  styleLibrary: {
    typography?: {
      h1?: { name?: string; url?: string; weight?: number };
      h2?: { name?: string; url?: string; weight?: number };
      h3?: { name?: string; url?: string; weight?: number };
      h4?: { name?: string; url?: string; weight?: number };
      body?: { name?: string; url?: string; weight?: number };
    };
    colors?: {
      neutrals: {
        light?: string;
        subtle?: string;
        muted?: string;
        dark?: string;
      };
      brand: {
        primary?: { color?: string };
        secondary?: { color?: string };
        tertiary?: { color?: string };
        accent?: { color?: string };
      };
    };
    cornerRounding?: number;
  },
): string {
  const styleLibraryCss = getStyleLibraryCss(styleLibrary);
  const styleTag = `<style data-style-library="starter-site-preview">\n${styleLibraryCss}</style>\n`;

  const lowerHtml = html.toLowerCase();
  const closingHeadTag = "</head>";
  const closingHeadIndex = lowerHtml.lastIndexOf(closingHeadTag);
  if (closingHeadIndex !== -1) {
    return `${html.slice(0, closingHeadIndex)}${styleTag}${html.slice(
      closingHeadIndex,
    )}`;
  }

  return `${styleTag}${html}`;
}

function getStyleLibraryCss(
  styleLibrary: Parameters<typeof injectStyleLibraryCssIntoHtml>[1],
): string {
  let css = "";

  if (styleLibrary.typography) {
    css += generateFontFaceCss(styleLibrary.typography);
    css += generateTypographyElementCss(styleLibrary.typography);
  }

  css += ":root {\n";

  if (styleLibrary.colors) {
    css += generateColorVariablesCss(styleLibrary.colors);
  }

  if (styleLibrary.cornerRounding !== undefined) {
    css += `  --corner-rounding: ${styleLibrary.cornerRounding}px;\n`;
  }

  css += "}\n";

  return css;
}

function generateFontFaceCss(
  typography: NonNullable<
    Parameters<typeof injectStyleLibraryCssIntoHtml>[1]["typography"]
  >,
): string {
  let css = "";

  const seenFontFaces = new Set<string>();
  for (const font of Object.values(typography)) {
    if (!font?.name || !font?.url) {
      continue;
    }

    const key = `${font.name}|${font.weight ?? 400}|${font.url}`;
    if (seenFontFaces.has(key)) {
      continue;
    }
    seenFontFaces.add(key);

    const fontName = escapeCssValue(font.name);
    const fontUrl = escapeCssValue(parseAndValidateHttpsUrl(font.url));

    css += `@font-face {\n  font-family: "${fontName}";\n  src: url('${fontUrl}') format('woff2');\n  font-weight: ${font.weight ?? 400};\n  font-style: normal;\n  font-display: swap;\n}\n\n`;
  }

  return css;
}

function generateTypographyElementCss(
  typography: NonNullable<
    Parameters<typeof injectStyleLibraryCssIntoHtml>[1]["typography"]
  >,
): string {
  let css = "";

  if (typography.body?.name) {
    const bodyFontName = escapeCssValue(typography.body.name);
    css += `html, body {\n  font-family: "${bodyFontName}", sans-serif;\n  font-weight: ${typography.body.weight ?? 400};\n}\n\n`;
  }

  const headings = [
    { tag: "h1", font: typography.h1 },
    { tag: "h2", font: typography.h2 },
    { tag: "h3", font: typography.h3 },
    { tag: "h4", font: typography.h4 },
  ];

  for (const heading of headings) {
    if (!heading.font?.name) {
      continue;
    }

    const headingFontName = escapeCssValue(heading.font.name);
    css += `${heading.tag} {\n  font-family: "${headingFontName}", sans-serif;\n  font-weight: ${heading.font.weight ?? 400};\n}\n\n`;
  }

  return css;
}

function generateColorVariablesCss(
  colors: NonNullable<
    Parameters<typeof injectStyleLibraryCssIntoHtml>[1]["colors"]
  >,
): string {
  let css = "";

  for (const [key, value] of Object.entries(colors.neutrals)) {
    if (value?.trim()) {
      css += `  --${key}: ${value};\n`;
    }
  }

  for (const [key, colorObj] of Object.entries(colors.brand)) {
    if (colorObj?.color?.trim()) {
      css += `  --${key}: ${colorObj.color};\n`;
    }
  }

  return css;
}

function escapeCssValue(value: string): string {
  return value
    .replaceAll("\\", "\\\\")
    .replaceAll('"', '\\"')
    .replaceAll("'", "\\'")
    .replaceAll("\n", " ")
    .replaceAll("\r", " ")
    .replaceAll("\f", " ");
}

function parseAndValidateHttpsUrl(value: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(`Invalid font URL in style library: ${value}`);
  }

  if (url.protocol !== "https:") {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(
      `Font URL in style library must use https, got ${url.protocol}: ${value}`,
    );
  }

  return url.toString();
}
